import React, {
    Children,
    cloneElement,
    forwardRef,
    isValidElement,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import './BootstrapDataGrid.css';
import LoadingElapsed from './LoadingElapsed';
import LocalizedDateInput from './LocalizedDateInput';
import { SABIT_LKP_EVENT } from '../utils/sabitLkp';
import {
    currentSabitItems,
    detectSabitStTur,
    isGeneralYetkiUser,
    openSabitDefinitions,
} from '../utils/sabitDefinitionAccess';
import { GridColumnLayoutBar, GridColumnTitle } from './GridColumnLayoutBar';
import { useGridColumnLayout } from './useGridColumnLayout';
import { columnPixelWidth } from '../utils/gridColumnLayout';

function createConfigComponent(role) {
    const Component = () => null;
    Component.gridRole = role;
    return Component;
}

export const Column = createConfigComponent('column');
export const Lookup = createConfigComponent('lookup');
export const Paging = createConfigComponent('paging');
export const Pager = createConfigComponent('pager');
export const Selection = createConfigComponent('selection');
export const Editing = createConfigComponent('editing');
export const Scrolling = createConfigComponent('scrolling');
export const LoadPanel = createConfigComponent('loadPanel');
export const FilterRow = createConfigComponent('filterRow');
export const HeaderFilter = createConfigComponent('headerFilter');
export const SearchPanel = createConfigComponent('searchPanel');
export const Grouping = createConfigComponent('grouping');
export const GroupPanel = createConfigComponent('groupPanel');
export const ColumnChooser = createConfigComponent('columnChooser');
export const ColumnFixing = createConfigComponent('columnFixing');
export const Export = createConfigComponent('export');
export const MasterDetail = createConfigComponent('masterDetail');
export const Toolbar = createConfigComponent('toolbar');
export const Item = createConfigComponent('item');

function childByRole(children, role) {
    return Children.toArray(children).find((child) => isValidElement(child) && child.type?.gridRole === role);
}

function childrenByRole(children, role) {
    return Children.toArray(children).filter((child) => isValidElement(child) && child.type?.gridRole === role);
}

function lookupForColumn(columnElement) {
    return childByRole(columnElement.props.children, 'lookup')?.props || null;
}

function normalizeColumns(children) {
    return childrenByRole(children, 'column').map((element, index) => ({
        ...element.props,
        lookup: lookupForColumn(element),
        _index: index,
    }));
}

function valueFromPath(row, field) {
    if (!field) return undefined;
    return String(field).split('.').reduce((value, key) => value?.[key], row);
}

function resolveExpr(item, expr) {
    if (typeof expr === 'function') return expr(item);
    return expr ? valueFromPath(item, expr) : item;
}

function lookupText(lookup, value) {
    if (!lookup) return value;
    const source = Array.isArray(lookup.dataSource) ? lookup.dataSource : [];
    const match = source.find((item) => Object.is(resolveExpr(item, lookup.valueExpr), value)
        || String(resolveExpr(item, lookup.valueExpr)) === String(value));
    return match ? resolveExpr(match, lookup.displayExpr) : value;
}

function formatDate(value, withTime) {
    if (value == null || value === '') return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    const formatted = new Intl.DateTimeFormat('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    }).format(date);
    return formatted.replace(/\//g, '-');
}

function formatNumber(value, format) {
    const number = Number(value);
    if (!Number.isFinite(number)) return value ?? '';
    const decimalPart = String(format || '').split('.')[1] || '';
    const minimumFractionDigits = (decimalPart.match(/0/g) || []).length;
    const maximumFractionDigits = Math.max(minimumFractionDigits, decimalPart.length || 2);
    return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits,
        maximumFractionDigits,
    }).format(number);
}

function booleanValue(value) {
    if (value === true || value === 1) return true;
    if (typeof value === 'string') {
        return ['true', '1', 'evet'].includes(value.trim().toLocaleLowerCase('tr-TR'));
    }
    return false;
}

function formatValue(value, column) {
    const lookedUp = lookupText(column.lookup, value);
    if (column.dataType === 'date' || column.dataType === 'datetime') {
        return formatDate(lookedUp, column.dataType === 'datetime' || String(column.format).includes('HH'));
    }
    if (column.dataType === 'number' || (typeof column.format === 'string' && column.format.includes('#'))) {
        return formatNumber(lookedUp, column.format);
    }
    if (column.dataType === 'boolean') return booleanValue(lookedUp) ? 'Evet' : 'Hayır';
    return lookedUp == null ? '' : String(lookedUp);
}

function inputDateValue(value, withTime) {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
    return withTime ? local.slice(0, 16) : local.slice(0, 10);
}

function compareValues(a, b) {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), 'tr', { numeric: true, sensitivity: 'base' });
}

function toolbarContent(toolbarElement) {
    if (!toolbarElement) return [];
    const declaredItems = Array.isArray(toolbarElement.props.items) ? toolbarElement.props.items : [];
    return [
        ...declaredItems,
        ...childrenByRole(toolbarElement.props.children, 'item').map((element) => element.props),
    ];
}

const BootstrapDataGrid = forwardRef(function BootstrapDataGrid({
    children,
    dataSource,
    keyExpr = 'id',
    height,
    width,
    className = '',
    focusedRowEnabled = false,
    focusedRowKey,
    defaultFocusedRowIndex = -1,
    selectedRowKeys,
    onFocusedRowChanged,
    onSelectionChanged,
    onRowClick,
    onRowDblClick,
    onCellClick,
    onContentReady,
    onRowPrepared,
    onRowCollapsing,
    onRowExpanding,
    onSaving,
    onRowRemoved,
    onOptionChanged,
    loading: externalLoading = false,
    noDataText = 'Kayıt bulunamadı',
    layoutKey = null,
}, ref) {
    const [sabitRevision, setSabitRevision] = useState(0);
    useEffect(() => {
        const refresh = () => setSabitRevision((revision) => revision + 1);
        window.addEventListener(SABIT_LKP_EVENT, refresh);
        return () => window.removeEventListener(SABIT_LKP_EVENT, refresh);
    }, []);
    const sourceColumns = useMemo(() => normalizeColumns(children)
        .map((column) => {
            const stTur = detectSabitStTur(column.lookup?.dataSource);
            return stTur
                ? { ...column, lookup: { ...column.lookup, dataSource: currentSabitItems(stTur) } }
                : column;
        }), [children, sabitRevision]);
    const columnLayout = useGridColumnLayout(layoutKey, sourceColumns);
    const columns = useMemo(
        () => columnLayout.columns.filter((column) => column.visible !== false),
        [columnLayout.columns],
    );
    const lockColumnWidths = columnLayout.canManage || columnLayout.layoutActive;
    const pagingElement = childByRole(children, 'paging');
    const paging = pagingElement?.props || {};
    const pager = childByRole(children, 'pager')?.props || {};
    const selection = childByRole(children, 'selection')?.props || {};
    const editing = childByRole(children, 'editing')?.props || {};
    const rowAllowsDelete = (row) => (
        typeof editing.allowDeleting === 'function' ? !!editing.allowDeleting(row) : !!editing.allowDeleting
    );
    const deleteColumnVisible = typeof editing.allowDeleting === 'function' || !!editing.allowDeleting;
    const searchPanel = childByRole(children, 'searchPanel')?.props || {};
    const filterRow = childByRole(children, 'filterRow')?.props || {};
    const masterDetail = childByRole(children, 'masterDetail')?.props || {};
    const exportOptions = childByRole(children, 'export')?.props || {};
    const toolbar = childByRole(children, 'toolbar');
    const toolbarItems = useMemo(() => toolbarContent(toolbar), [toolbar]);
    const [loadedRows, setLoadedRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [searchText, setSearchText] = useState('');
    const [columnFilters, setColumnFilters] = useState({});
    const [sort, setSort] = useState(() => {
        const initial = columns.filter((column) => column.sortOrder)
            .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0))[0];
        return initial ? { field: initial.dataField, desc: initial.sortOrder === 'desc' } : null;
    });
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(paging.pageSize || paging.defaultPageSize || pager.pageSize || 20);
    const [totalCount, setTotalCount] = useState(0);
    const [internalFocusedKey, setInternalFocusedKey] = useState(null);
    const focusedKeyRef = useRef(null);
    const [internalSelectedKeys, setInternalSelectedKeys] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [collapsedGroupKeys, setCollapsedGroupKeys] = useState([]);
    const expandedKeysRef = useRef(expandedKeys);
    const [changes, setChanges] = useState([]);
    const changesRef = useRef(changes);
    const rowsRef = useRef([]);
    const instanceRef = useRef(null);
    const isRemote = !Array.isArray(dataSource) && typeof dataSource?.load === 'function';

    useEffect(() => {
        changesRef.current = changes;
        onOptionChanged?.({ fullName: 'editing.changes', value: changes });
    }, [changes, onOptionChanged]);

    useEffect(() => {
        expandedKeysRef.current = expandedKeys;
    }, [expandedKeys]);

    const reload = useCallback(async () => {
        if (!isRemote) {
            const rows = Array.isArray(dataSource) ? dataSource : [];
            setLoadedRows(rows);
            setTotalCount(rows.length);
            return rows;
        }
        setLoading(true);
        setLoadError('');
        try {
            const remoteFilter = Object.entries(columnFilters)
                .filter(([, value]) => value != null && value !== '')
                .reduce((combined, [field, value]) => {
                    const condition = [field, 'contains', value];
                    return combined ? [combined, 'and', condition] : condition;
                }, null);
            const result = await dataSource.load({
                skip: pageIndex * pageSize,
                take: pageSize,
                requireTotalCount: true,
                searchValue: searchText || null,
                filter: remoteFilter,
                sort: sort?.field ? [{ selector: sort.field, desc: Boolean(sort?.desc) }] : undefined,
            });
            const rows = Array.isArray(result) ? result : (result?.data || []);
            setLoadedRows(rows);
            setTotalCount(Number(result?.totalCount ?? rows.length));
            return rows;
        } catch (error) {
            setLoadedRows([]);
            setTotalCount(0);
            setLoadError(error?.message || 'Veriler yüklenemedi');
            return [];
        } finally {
            setLoading(false);
        }
    }, [columnFilters, dataSource, isRemote, pageIndex, pageSize, searchText, sort]);

    useEffect(() => {
        void reload();
    }, [reload]);

    const patchedRows = useMemo(() => loadedRows
        .filter((row) => !changes.some((change) => change.type === 'remove' && String(change.key) === String(valueFromPath(row, keyExpr))))
        .map((row) => {
            const key = valueFromPath(row, keyExpr);
            const update = changes.find((change) => change.type === 'update' && String(change.key) === String(key));
            return update ? { ...row, ...update.data } : row;
        }), [changes, keyExpr, loadedRows]);

    const visibleRows = useMemo(() => {
        let rows = [...patchedRows];
        if (!isRemote && searchText) {
            const needle = searchText.toLocaleLowerCase('tr-TR');
            rows = rows.filter((row) => columns.some((column) =>
                formatValue(valueFromPath(row, column.dataField), column).toLocaleLowerCase('tr-TR').includes(needle)));
        }
        if (!isRemote) {
            Object.entries(columnFilters).forEach(([field, needle]) => {
                if (!needle) return;
                rows = rows.filter((row) => String(valueFromPath(row, field) ?? '')
                    .toLocaleLowerCase('tr-TR').includes(String(needle).toLocaleLowerCase('tr-TR')));
            });
            if (sort?.field) {
                rows.sort((left, right) => compareValues(valueFromPath(left, sort.field), valueFromPath(right, sort.field))
                    * (sort?.desc ? -1 : 1));
            }
        }
        return rows;
    }, [columnFilters, columns, isRemote, patchedRows, searchText, sort]);

    const pagingEnabled = Boolean(pagingElement) && paging.enabled !== false;
    const pageCount = Math.max(1, Math.ceil((isRemote ? totalCount : visibleRows.length) / pageSize));
    const renderedRows = !isRemote && pagingEnabled
        ? visibleRows.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize)
        : visibleRows;
    const groupColumn = columns.filter((column) => column.groupIndex != null)
        .sort((left, right) => left.groupIndex - right.groupIndex)[0] || null;
    const tableColumns = groupColumn ? columns.filter((column) => column !== groupColumn) : columns;
    const groupedRows = groupColumn
        ? renderedRows.reduce((groups, row) => {
            const value = valueFromPath(row, groupColumn.dataField);
            const key = String(value ?? '');
            const current = groups[groups.length - 1];
            if (current?.key === key) current.rows.push(row);
            else groups.push({ key, value, rows: [row] });
            return groups;
        }, [])
        : [{ key: '', value: null, rows: renderedRows }];
    rowsRef.current = renderedRows;

    useEffect(() => {
        if (pageIndex >= pageCount) setPageIndex(Math.max(0, pageCount - 1));
    }, [pageCount, pageIndex]);

    const activeFocusedKey = focusedRowKey !== undefined ? focusedRowKey : internalFocusedKey;
    const activeSelectedKeys = selectedRowKeys !== undefined ? selectedRowKeys : internalSelectedKeys;
    const selectionVisible = selection.mode === 'multiple' || Boolean(selection.showCheckBoxesMode);
    focusedKeyRef.current = activeFocusedKey;

    const option = useCallback((name, value) => {
        const rows = rowsRef.current;
        if (value === undefined) {
            if (name === 'focusedRowKey') return focusedKeyRef.current;
            if (name === 'focusedRowIndex') return rows.findIndex((row) =>
                String(valueFromPath(row, keyExpr)) === String(focusedKeyRef.current));
            if (name === 'editing.changes') return changesRef.current;
            return undefined;
        }
        if (name === 'focusedRowKey') {
            focusedKeyRef.current = value;
            setInternalFocusedKey(value);
        }
        if (name === 'focusedRowIndex') {
            const row = rows[value];
            focusedKeyRef.current = row ? valueFromPath(row, keyExpr) : null;
            setInternalFocusedKey(row ? valueFromPath(row, keyExpr) : null);
        }
        return value;
    }, [keyExpr]);

    const cancelEditData = useCallback(() => setChanges([]), []);
    const saveEditData = useCallback(async () => {
        const event = { changes: changesRef.current, cancel: false, component: instanceRef.current };
        await onSaving?.(event);
        if (event.promise) await event.promise;
        if (!event.cancel) {
            changesRef.current.filter((change) => change.type === 'remove').forEach((change) => {
                const row = rowsRef.current.find((item) => String(valueFromPath(item, keyExpr)) === String(change.key));
                if (row) onRowRemoved?.({ data: row, key: change.key, component: instanceRef.current });
            });
            setChanges([]);
        }
    }, [keyExpr, onRowRemoved, onSaving]);

    const instance = useMemo(() => ({
        option,
        refresh: reload,
        repaintRows: () => undefined,
        cancelEditData,
        saveEditData,
        getVisibleRows: () => rowsRef.current.map((data, rowIndex) => ({
            data,
            key: valueFromPath(data, keyExpr),
            rowIndex,
            rowType: 'data',
        })),
        getSelectedRowsData: () => rowsRef.current.filter((row) =>
            activeSelectedKeys.some((key) => String(key) === String(valueFromPath(row, keyExpr)))),
        clearSelection: () => setInternalSelectedKeys([]),
        selectRows: (keys = [], preserve = false) => setInternalSelectedKeys((current) => {
            const next = preserve ? Array.from(new Set([...current, ...keys])) : keys;
            return current.length === next.length && current.every((key, index) => String(key) === String(next[index]))
                ? current
                : next;
        }),
        getDataSource: () => ({ reload, items: () => rowsRef.current }),
        isRowExpanded: (key) => expandedKeysRef.current.some((item) => String(item) === String(key)),
        expandRow: (key) => setExpandedKeys((current) => current.some((item) => String(item) === String(key))
            ? current
            : [...current, key]),
        collapseRow: (key) => setExpandedKeys((current) => current.filter((item) => String(item) !== String(key))),
    }), [activeSelectedKeys, cancelEditData, keyExpr, option, reload, saveEditData]);
    instanceRef.current = instance;
    useImperativeHandle(ref, () => ({ instance }), [instance]);

    const focusRow = useCallback((row, rowIndex, nativeEvent) => {
        const key = valueFromPath(row, keyExpr);
        focusedKeyRef.current = key;
        setInternalFocusedKey(key);
        if (selection.mode === 'multiple') {
            const next = activeSelectedKeys.some((item) => String(item) === String(key))
                ? activeSelectedKeys.filter((item) => String(item) !== String(key))
                : [...activeSelectedKeys, key];
            setInternalSelectedKeys(next);
            onSelectionChanged?.({ selectedRowKeys: next, selectedRowsData: rowsRef.current.filter((item) =>
                next.some((selected) => String(selected) === String(valueFromPath(item, keyExpr)))), component: instance });
        } else if (selection.mode === 'single') {
            setInternalSelectedKeys([key]);
            onSelectionChanged?.({ selectedRowKeys: [key], selectedRowsData: [row], component: instance });
        }
        if (focusedRowEnabled || onFocusedRowChanged) {
            onFocusedRowChanged?.({ row: { data: row, rowIndex, key }, rowIndex, component: instance });
        }
        onRowClick?.({ data: row, key, rowIndex, component: instance, event: nativeEvent });
    }, [activeSelectedKeys, focusedRowEnabled, instance, keyExpr, onFocusedRowChanged, onRowClick, onSelectionChanged, selection.mode]);

    useEffect(() => {
        onContentReady?.({ component: instance, element: null });
    }, [instance, loadedRows, onContentReady]);

    useEffect(() => {
        if (defaultFocusedRowIndex < 0 || activeFocusedKey != null) return;
        const row = rowsRef.current[defaultFocusedRowIndex];
        if (!row) return;
        const key = valueFromPath(row, keyExpr);
        setInternalFocusedKey(key);
        onFocusedRowChanged?.({
            row: { data: row, rowIndex: defaultFocusedRowIndex, key },
            rowIndex: defaultFocusedRowIndex,
            component: instance,
        });
    }, [activeFocusedKey, defaultFocusedRowIndex, instance, keyExpr, loadedRows, onFocusedRowChanged]);

    const updateCell = useCallback((row, column, rawValue) => {
        const key = valueFromPath(row, keyExpr);
        let value = rawValue;
        if (column.dataType === 'number') value = rawValue === '' ? null : Number(rawValue);
        if (column.dataType === 'boolean') value = Boolean(rawValue);
        const existing = changesRef.current.find((change) => change.type === 'update' && String(change.key) === String(key));
        const next = existing
            ? changesRef.current.map((change) => change === existing ? { ...change, data: { ...change.data, [column.dataField]: value } } : change)
            : [...changesRef.current, { type: 'update', key, data: { [column.dataField]: value } }];
        setChanges(next);
    }, [keyExpr]);

    const removeRow = useCallback((row) => {
        const key = valueFromPath(row, keyExpr);
        setChanges((current) => {
            const withoutUpdate = current.filter((change) => !(change.type === 'update' && String(change.key) === String(key)));
            return [...withoutUpdate, { type: 'remove', key }];
        });
    }, [keyExpr]);

    const renderEditor = (row, column) => {
        const value = valueFromPath(row, column.dataField);
        if (column.lookup) {
            const source = Array.isArray(column.lookup.dataSource) ? column.lookup.dataSource : [];
            const stTur = detectSabitStTur(source);
            return (
                <div className="d-flex align-items-stretch gap-1" onClick={(event) => event.stopPropagation()}>
                    <select className="form-select form-select-sm" value={value ?? ''} onChange={(event) => updateCell(row, column, event.target.value)}>
                        <option value="">Seçiniz</option>
                        {source.map((item, index) => {
                            const itemValue = resolveExpr(item, column.lookup.valueExpr);
                            return <option key={`${itemValue}-${index}`} value={itemValue ?? ''}>{resolveExpr(item, column.lookup.displayExpr)}</option>;
                        })}
                    </select>
                    {stTur && isGeneralYetkiUser() && <button type="button" className="btn btn-sm btn-success px-2"
                        title="Sabit tanım ekranı" aria-label="Sabit tanım ekranı"
                        onClick={() => openSabitDefinitions(stTur)}>T</button>}
                </div>
            );
        }
        if (column.dataType === 'boolean') {
            return <input className="form-check-input" type="checkbox" checked={booleanValue(value)} onChange={(event) => updateCell(row, column, event.target.checked)} />;
        }
        const withTime = column.dataType === 'datetime' || String(column.format).includes('HH');
        const type = column.dataType === 'number' ? 'number' : column.dataType === 'date' || withTime ? (withTime ? 'datetime-local' : 'date') : 'text';
        const inputValue = type === 'date' || type === 'datetime-local' ? inputDateValue(value, withTime) : (value ?? '');
        return <input className="form-control form-control-sm" type={type} value={inputValue} onChange={(event) => updateCell(row, column, event.target.value)} />;
    };

    const renderToolbarItem = (item, index) => {
        if (item.visible === false || item.options?.visible === false) return null;
        if (item.name === 'exportButton') {
            return <button key={index} type="button" className="btn btn-sm btn-outline-success"
                onClick={() => {
                    const header = tableColumns.map((column) => column.caption || column.dataField || '');
                    const lines = rowsRef.current.map((row) => tableColumns.map((column) => {
                        const text = formatValue(valueFromPath(row, column.dataField), column);
                        return `"${String(text).replace(/"/g, '""')}"`;
                    }).join(';'));
                    const blob = new Blob([`\uFEFF${[header.join(';'), ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const anchor = document.createElement('a');
                    anchor.href = url;
                    anchor.download = `${exportOptions.fileName || 'liste'}.csv`;
                    anchor.click();
                    URL.revokeObjectURL(url);
                }}>{item.options?.text || 'Dışa Aktar'}</button>;
        }
        if (typeof item.render === 'function') return <React.Fragment key={index}>{item.render()}</React.Fragment>;
        if (isValidElement(item.children)) return cloneElement(item.children, { key: index });
        const options = item.options || {};
        if (item.widget === 'dxSelectBox') {
            const source = Array.isArray(options.dataSource) ? options.dataSource : (Array.isArray(options.items) ? options.items : []);
            return (
                <select key={index} className="form-select form-select-sm" style={{ width: options.width || 'auto' }}
                    value={options.value ?? ''} disabled={options.disabled}
                    aria-label={options.inputAttr?.['aria-label'] || options.placeholder || 'Seçiniz'}
                    onChange={(event) => options.onValueChanged?.({ value: event.target.value })}>
                    <option value="">{options.placeholder || 'Seçiniz'}</option>
                    {source.map((entry, entryIndex) => {
                        const value = resolveExpr(entry, options.valueExpr);
                        return <option key={`${value}-${entryIndex}`} value={value ?? ''}>{resolveExpr(entry, options.displayExpr)}</option>;
                    })}
                </select>
            );
        }
        if (item.widget === 'dxTextBox') {
            return <input key={index} type="search" className="form-control form-control-sm"
                style={{ width: options.width || 'auto' }} value={options.value ?? ''}
                placeholder={options.placeholder || 'Ara...'}
                onChange={(event) => options.onValueChanged?.({ value: event.target.value })}
                onKeyDown={(event) => options.onKeyDown?.({
                    event,
                    component: { option: (name) => name === 'value' ? event.currentTarget.value : undefined },
                })} />;
        }
        if (item.widget === 'dxDateBox') {
            const withTime = options.type === 'datetime' || String(options.displayFormat || '').includes('HH');
            return <div key={index} style={{ width: options.width || 150 }}>
                <LocalizedDateInput
                    value={options.value}
                    withTime={withTime}
                    placeholder={options.placeholder}
                    min={options.min}
                    max={options.max}
                    onValueChange={(value) => options.onValueChanged?.({ value })}
                />
            </div>;
        }
        if (item.widget === 'dxButton') {
            return <button key={index} type="button" className="btn btn-sm btn-primary" disabled={options.disabled} onClick={options.onClick}>{options.text || 'Çalıştır'}</button>;
        }
        return null;
    };

    return (
        <div className={`bootstrap-data-grid ${className}`} style={{ width: width || '100%' }}>
            {columnLayout.canManage && (
                <div className="bootstrap-data-grid__toolbar">
                    <GridColumnLayoutBar
                        enabled
                        columns={columnLayout.columns}
                        onMoveBy={columnLayout.moveBy}
                        onToggle={columnLayout.setVisible}
                        onWidth={columnLayout.resize}
                        onSave={columnLayout.save}
                        onReset={columnLayout.reset}
                    />
                </div>
            )}
            {(toolbarItems.length > 0 || searchPanel.visible) && (
                <div className="bootstrap-data-grid__toolbar">
                    {toolbarItems.map(renderToolbarItem)}
                    {searchPanel.visible && (
                        <input className="form-control form-control-sm bootstrap-data-grid__search" type="search"
                            value={searchText} placeholder={searchPanel.placeholder || 'Ara...'}
                            onChange={(event) => { setSearchText(event.target.value); setPageIndex(0); }} />
                    )}
                </div>
            )}
            <div className="bootstrap-data-grid__scroll" style={{ height: height || undefined }}>
                {(loading || externalLoading) && <div className="bootstrap-data-grid__loading"><span className="spinner-border spinner-border-sm" /> <LoadingElapsed /></div>}
                <table className={`table table-sm table-hover table-bordered align-middle mb-0 bootstrap-data-grid__table${lockColumnWidths ? ' is-sized' : ''}`}>
                    <thead>
                        <tr>
                            {selectionVisible && <th className="bootstrap-data-grid__check-col" aria-label="Seçim" />}
                            {masterDetail.enabled && <th className="bootstrap-data-grid__detail-col" aria-label="Detay" />}
                            {tableColumns.map((column) => {
                                const px = columnPixelWidth(column) || (lockColumnWidths ? 120 : null);
                                return (
                                <th key={column._layoutId || `${column.dataField || 'column'}-${column._index}`}
                                    className={column.allowSorting === false ? '' : 'bootstrap-data-grid__sortable'}
                                    style={px ? { width: px, minWidth: px, maxWidth: px } : { width: column.width, minWidth: column.minWidth }}
                                    onClick={() => {
                                        if (column.allowSorting === false || !column.dataField) return;
                                        setSort((current) => current?.field === column.dataField
                                            ? { field: column.dataField, desc: !current?.desc }
                                            : { field: column.dataField, desc: false });
                                    }}>
                                    <GridColumnTitle
                                        column={column}
                                        extra={sort?.field === column.dataField ? (sort?.desc ? ' ▼' : ' ▲') : null}
                                        canManage={columnLayout.canManage}
                                        onResize={columnLayout.resize}
                                        onDropColumn={columnLayout.move}
                                    />
                                </th>
                                );
                            })}
                            {deleteColumnVisible && <th className="bootstrap-data-grid__actions-col">İşlem</th>}
                        </tr>
                        {filterRow.visible && (
                            <tr className="bootstrap-data-grid__filters">
                                {selectionVisible && <th />}
                                {masterDetail.enabled && <th />}
                                {tableColumns.map((column) => (
                                    <th key={`${column.dataField || 'column'}-${column._index}-filter`}>
                                        {column.dataField && column.allowFiltering !== false && <input className="form-control form-control-sm" value={columnFilters[column.dataField] || ''}
                                            aria-label={`${column.caption || column.dataField} filtre`}
                                            onChange={(event) => {
                                                const value = event.target.value;
                                                setColumnFilters((current) => ({ ...current, [column.dataField]: value }));
                                                setPageIndex(0);
                                                onOptionChanged?.({
                                                    fullName: `columns[${column._index}].filterValue`,
                                                    value,
                                                    component: instance,
                                                });
                                            }} />}
                                    </th>
                                ))}
                                {deleteColumnVisible && <th />}
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {groupedRows.map((group) => {
                            const groupCollapsed = collapsedGroupKeys.includes(group.key);
                            return (
                                <React.Fragment key={`group-${group.key}`}>
                                    {groupColumn && (
                                        <tr className="bootstrap-data-grid__group-row">
                                            <td colSpan={Math.max(1, tableColumns.length + (selectionVisible ? 1 : 0) + (masterDetail.enabled ? 1 : 0) + (deleteColumnVisible ? 1 : 0))}>
                                                <button type="button" className="btn btn-sm btn-link text-start p-0"
                                                    onClick={() => {
                                                        if (groupCollapsed) {
                                                            onRowExpanding?.({ key: group.value, component: instance });
                                                            setCollapsedGroupKeys((current) => current.filter((key) => key !== group.key));
                                                        } else {
                                                            onRowCollapsing?.({ key: group.value, component: instance });
                                                            setCollapsedGroupKeys((current) => [...current, group.key]);
                                                        }
                                                    }}>
                                                    {groupCollapsed ? '▶' : '▼'}{' '}
                                                    {groupColumn.groupCellRender
                                                        ? groupColumn.groupCellRender({ data: { items: group.rows }, value: group.value })
                                                        : `${groupColumn.caption || groupColumn.dataField}: ${formatValue(group.value, groupColumn)}`}
                                                    {' '}({group.rows.length})
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                    {!groupCollapsed && group.rows.map((row) => {
                                        const rowIndex = renderedRows.indexOf(row);
                                        const key = valueFromPath(row, keyExpr);
                                        const selected = activeSelectedKeys.some((item) => String(item) === String(key));
                                        const focused = String(activeFocusedKey) === String(key);
                                        const expanded = expandedKeys.some((item) => String(item) === String(key));
                                        return (
                                            <React.Fragment key={key ?? rowIndex}>
                                                <tr className={`${selected ? 'table-primary' : ''} ${focused ? 'bootstrap-data-grid__focused' : ''}`}
                                                    ref={(element) => element && onRowPrepared?.({ rowType: 'data', data: row, rowElement: element, rowIndex, component: instance })}
                                                    onClick={(event) => focusRow(row, rowIndex, event)}
                                                    onDoubleClick={(event) => onRowDblClick?.({ data: row, key, rowIndex, component: instance, event })}>
                                                    {selectionVisible && <td data-label="Seçim"><input className="form-check-input"
                                                        type={selection.mode === 'multiple' ? 'checkbox' : 'radio'} checked={selected} readOnly /></td>}
                                                    {masterDetail.enabled && <td data-label="Detay"><button type="button" className="btn btn-sm btn-link p-0"
                                                        aria-label={expanded ? 'Detayı kapat' : 'Detayı aç'}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setExpandedKeys((current) => expanded
                                                                ? current.filter((item) => String(item) !== String(key))
                                                                : [...current, key]);
                                                        }}>{expanded ? '−' : '+'}</button></td>}
                                                    {tableColumns.map((column) => {
                                                        const value = valueFromPath(row, column.dataField);
                                                        const editable = editing.allowUpdating && column.allowEditing !== false && column.enabled !== false && column.dataField;
                                                        const content = editable
                                                            ? renderEditor(row, column)
                                                            : column.cellRender
                                                                ? column.cellRender({ data: row, value, displayValue: formatValue(value, column), rowIndex, column })
                                                                : column.dataType === 'boolean'
                                                                    ? <input className="form-check-input" type="checkbox"
                                                                        checked={booleanValue(value)} readOnly
                                                                        aria-label={`${column.caption || column.dataField}: ${booleanValue(value) ? 'Evet' : 'Hayır'}`} />
                                                                    : formatValue(value, column);
                                                        return <td key={`${column.dataField || 'column'}-${column._index}`} data-label={column.caption || column.dataField || ''}
                                                            className={column.cssClass || ''} style={{ textAlign: column.alignment }}
                                                            onClick={(event) => onCellClick?.({
                                                                data: row,
                                                                value,
                                                            displayValue: formatValue(value, column),
                                                                key,
                                                                rowType: 'data',
                                                            row: { data: row, key, rowIndex },
                                                                rowIndex,
                                                                column,
                                                                columnIndex: column._index,
                                                                cellElement: event.currentTarget,
                                                                component: instance,
                                                                event,
                                                            })}>{content}</td>;
                                                    })}
                                                    {deleteColumnVisible && <td data-label="İşlem">{rowAllowsDelete(row) && <button type="button" className="btn btn-sm btn-outline-danger"
                                                        onClick={(event) => { event.stopPropagation(); removeRow(row); }}>Sil</button>}</td>}
                                                </tr>
                                                {expanded && masterDetail.render && (
                                                    <tr className="bootstrap-data-grid__detail-row">
                                                        <td colSpan={tableColumns.length + (selectionVisible ? 1 : 0) + 1}>
                                                            {masterDetail.render({ data: row, key })}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                        {!loading && renderedRows.length === 0 && (
                            <tr><td className="bootstrap-data-grid__empty" colSpan={Math.max(1, tableColumns.length + 2)}>{loadError || noDataText}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            {pagingEnabled && (
                <div className="bootstrap-data-grid__pager">
                    <span>{totalCount || visibleRows.length} kayıt · {pageIndex + 1}/{pageCount} sayfa</span>
                    <div className="btn-group btn-group-sm">
                        <button type="button" className="btn btn-outline-secondary" disabled={pageIndex === 0} onClick={() => setPageIndex((value) => Math.max(0, value - 1))}>Önceki</button>
                        <button type="button" className="btn btn-outline-secondary" disabled={pageIndex >= pageCount - 1} onClick={() => setPageIndex((value) => Math.min(pageCount - 1, value + 1))}>Sonraki</button>
                    </div>
                    {(pager.allowedPageSizes || pager.showPageSizeSelector) && (
                        <select className="form-select form-select-sm bootstrap-data-grid__page-size" value={pageSize}
                            onChange={(event) => { setPageSize(Number(event.target.value)); setPageIndex(0); }}>
                            {(pager.allowedPageSizes || [15, 25, 50]).map((size) => <option key={size} value={size}>{size}</option>)}
                        </select>
                    )}
                </div>
            )}
        </div>
    );
});

export default BootstrapDataGrid;
